import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import { getPopularTags, searchNews } from "@/utils/news";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") ?? "";
  const [keyword, setKeyword] = useState(initialQ);
  const [submitted, setSubmitted] = useState(initialQ);

  useEffect(() => {
    const q = params.get("q") ?? "";
    setKeyword(q);
    setSubmitted(q);
  }, [params]);

  const results = useMemo(() => searchNews(submitted), [submitted]);
  const suggestions = useMemo(() => getPopularTags(8), []);
  const relatedSuggestions = suggestions.filter((s) => s !== submitted).slice(0, 6);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSubmitted(q);
    setParams(q ? { q } : {});
  };

  return (
    <Layout>
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-5 md:py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

        <div className="mt-4 rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 p-5 md:p-7">
          <h1 className="text-xl md:text-2xl font-black text-background-50">
            {submitted ? (
              <>
                搜索 “<span className="text-accent-300">{submitted}</span>”
              </>
            ) : (
              "全站搜索"
            )}
          </h1>
          <form onSubmit={handleSubmit} className="mt-4 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入明星名字、事件或关键词"
                className="w-full h-11 pl-11 pr-4 rounded-full bg-background-50 border border-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-accent-400"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-7 rounded-full bg-secondary-900 text-background-50 text-sm font-medium hover:bg-secondary-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
        </div>

        {/* Related search suggestions */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="text-xs text-foreground-400">相关搜索：</span>
          {relatedSuggestions.map((tag) => (
            <button
              key={tag}
              type="button"
              onClick={() => {
                setKeyword(tag);
                setSubmitted(tag);
                setParams({ q: tag });
              }}
              className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              {tag}
            </button>
          ))}
        </div>

        {/* Result count */}
        {submitted && (
          <p className="mt-6 text-sm text-foreground-500">
            共找到 <span className="font-bold text-primary-600">{results.length}</span> 条与 “
            {submitted}” 相关的资讯
          </p>
        )}

        {/* Results */}
        {!submitted ? (
          <div className="mt-6 flex flex-col items-center justify-center py-20 rounded-lg bg-background-100 border border-background-200 text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-3xl">
              <i className="ri-search-line" />
            </span>
            <p className="mt-4 text-foreground-700 font-medium">输入关键词开始搜索</p>
            <p className="mt-1 text-sm text-foreground-400 max-w-md">
              可以试试搜索“顶流男星”“巡演”“综艺”等热门词汇
            </p>
          </div>
        ) : results.length > 0 ? (
          <div className="mt-4 flex flex-col gap-3 md:gap-4">
            {results.map((article) => (
              <NewsCard key={article.id} article={article} variant="horizontal" />
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center py-20 rounded-lg bg-background-100 border border-background-200 text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-3xl">
              <i className="ri-emotion-sad-line" />
            </span>
            <p className="mt-4 text-foreground-700 font-medium">没有找到相关内容</p>
            <p className="mt-1 text-sm text-foreground-400 max-w-md">
              换个关键词试试，或者返回首页浏览最新娱乐资讯
            </p>
            <Link
              to="/"
              className="mt-5 h-10 px-5 inline-flex items-center gap-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
}